<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import IconWithHint from '@/components/IconWithHint.vue'
import { ICountry } from '@/api/countries'
import { IFeedAttributes } from '@/interfaces/feeds'
import { IDspMinimalCpm } from '@/apps/traforama/interfaces/dspMinimalCpms'
import {
  IRecommendedPrice,
  IRecommendedPricesTargeting
} from '@/apps/traforama/interfaces/recomendedPrices'
import { fetchItemsByTargeting } from '@/apps/traforama/api/recomendedPrices'
import { fetchItems as fetchDspMinimalCpms } from '@/apps/traforama/api/dspMinimalCpms'
import { useCountriesStore } from '@/store/modules/countries'

const props = defineProps<{
  feed: IFeedAttributes
}>()

const countriesStore = useCountriesStore()
const loading = ref(false)
const targeting = ref<IRecommendedPricesTargeting>()
const recommendedPrices = ref<Record<string, IRecommendedPrice>>({})
const minimalCpms = ref<Record<string, IDspMinimalCpm>>({})

const updateRates = async () => {
  const current: IRecommendedPricesTargeting = {
    type_id: props.feed.type_ids,
    stream: props.feed.stream,
    country_codes: [...props.feed.country_codes],
    device_ids: [...props.feed.device_ids]
  }

  loading.value = true
  try {
    const [, prices, minimals] = await Promise.all([
      countriesStore.GetItems(),
      fetchItemsByTargeting(current),
      fetchDspMinimalCpms(current.type_id)
    ])
    recommendedPrices.value = Object.fromEntries(
      prices.data.map((item) => [item.country_id, item])
    )
    minimalCpms.value = Object.fromEntries(
      minimals.data.map((item) => [item.attributes.country_id, item])
    )
    targeting.value = current
  } finally {
    loading.value = false
  }
}

const countries = computed(() => {
  if (!targeting.value) return []

  const codes = targeting.value.country_codes
  const targetCountries: ICountry[] = codes.length
    ? countriesStore.byIso(codes)
    : countriesStore.sorted
  const hasRecommendations = Object.keys(recommendedPrices.value).length > 0

  return targetCountries
    .map((country) => {
      const minimalCpm = minimalCpms.value[country.id]?.attributes
      const minimal = minimalCpm?.value ?? 0
      const maximal = minimalCpm?.value_max ?? 0
      const recommended = recommendedPrices.value[country.id]?.value ?? 0

      return {
        id: country.id,
        country: country.attributes.name,
        minimal,
        maximal,
        recommended: Math.max(recommended, minimal)
      }
    })
    .filter(
      (row) =>
        !hasRecommendations ||
        row.minimal > 0 ||
        row.recommended > 0 ||
        row.maximal > 0
    )
    .sort((a, b) => {
      if (a.country < b.country) return -1
      if (a.country > b.country) return 1
      return 0
    })
})

onMounted(() => {
  if (props.feed.id) updateRates()
})
</script>

<template>
  <el-form-item label="Recommended CPM">
    <div class="recommended-prices">
      <el-table
        v-loading="loading"
        :data="countries"
        max-height="400"
        empty-text=""
        class="recommended-prices-table"
      >
        <el-table-column prop="country" label="Country" />
        <el-table-column
          label="Recommended CPM, $"
          width="180"
          class-name="recommended-cpm-column"
          label-class-name="recommended-cpm-column"
        >
          <template #default="{ row }">
            <el-tooltip
              effect="dark"
              placement="top"
              content="Average CPM for the chosen geo and device. You may set your CPM as recommended or higher to get more traffic. Gets updated daily."
            >
              <span class="recommended-cpm">
                {{ row.recommended.toFixed(3) }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
      <div class="update-rates">
        <el-button
          type="primary"
          plain
          :loading="loading"
          @click.prevent="updateRates"
        >
          Update the rates
        </el-button>
        <IconWithHint>
          Make sure to update the Recommended CPMs if you are adding a new feed
          or in case you have changed the target settings for the existing feed.
        </IconWithHint>
      </div>
    </div>
  </el-form-item>
</template>

<style lang="scss" scoped>
.recommended-prices {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.recommended-prices-table {
  width: 100%;
  max-width: 430px;

  :deep(td.recommended-cpm-column),
  :deep(tr:hover td.recommended-cpm-column) {
    background-color: #ecf5ff !important;
  }

  :deep(th.recommended-cpm-column) {
    color: #409eff;
  }
}

.recommended-cpm {
  font-weight: bold;
}

.update-rates {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
